import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const CYCLE_FR = { mensuel: "Mensuel", trimestriel: "Trimestriel", semestriel: "Semestriel",
  annuel: "Annuel", ponctuel: "Ponctuel" };

const fmt = (type, v) => {
  if (v == null || v === "") return "—";
  if (type === "date") return new Date(v).toLocaleDateString("fr-FR");
  if (type === "bool") return v ? "Oui" : "Non";
  if (type === "cycle") return CYCLE_FR[v] || v;
  return String(v);
};

// Écarts entre la saisie (manuel / Excel) et le PDF analysé par l'IA.
export const AiDiscrepanciesCard = ({ contract, onRefresh, canWrite }) => {
  const [busy, setBusy] = useState(false);
  const gaps = contract.ai_discrepancies || [];
  if (!gaps.length) return null;

  const resolve = async (apply, dismiss) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/contracts/${contract.id}/ai-discrepancies`, { apply, dismiss });
      toast.success(data.applied.length ? `${data.applied.length} champ(s) remplacé(s)` : "Saisie conservée");
      onRefresh();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  const all = gaps.map((g) => g.field);

  return (
    <Card className="p-4 border-amber-200 bg-amber-50/40" data-testid="ai-discrepancies-card">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="text-sm font-semibold text-amber-900 flex items-center gap-1.5">
          <AlertTriangle className="h-4 w-4" /> {gaps.length} écart(s) entre ta saisie et le PDF
        </div>
        {canWrite && <div className="flex gap-1.5">
          <Button size="sm" variant="outline" disabled={busy} onClick={() => resolve([], all)}
                  data-testid="ai-gaps-keep-all">Tout garder</Button>
          <Button size="sm" disabled={busy} onClick={() => resolve(all, [])}
                  className="bg-amber-600 hover:bg-amber-700" data-testid="ai-gaps-apply-all">Tout remplacer</Button>
        </div>}
      </div>
      <div className="divide-y divide-amber-100 bg-white rounded border border-amber-100">
        {gaps.map((g) => (
          <div key={g.field} className="flex items-center gap-3 p-2 text-xs" data-testid={`ai-gap-${g.field}`}>
            <span className="w-40 text-slate-500">{g.label}</span>
            <span className="flex-1">Ta saisie : <b>{fmt(g.type, g.current)}</b></span>
            <span className="flex-1">PDF : <b className="text-blue-700">{fmt(g.type, g.extracted)}</b></span>
            {canWrite && <>
              <Button size="sm" variant="ghost" className="h-7 text-xs" disabled={busy}
                      onClick={() => resolve([], [g.field])} data-testid={`ai-gap-keep-${g.field}`}>Garder</Button>
              <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy}
                      onClick={() => resolve([g.field], [])} data-testid={`ai-gap-apply-${g.field}`}>Remplacer</Button>
            </>}
          </div>
        ))}
      </div>
    </Card>
  );
};
